// =============================================
// IndexingPanel — krok "Indeksowanie" pipeline'u (po Publikacji).
//
// Workflow partiami: publikujesz wpisy → tu pojawiają się ich PRAWDZIWE URL-e
// z WP (wp_post_url z DB, nie zgadywane slugi) → kopiujesz do schowka / CSV →
// wrzucasz do indexera → klik "Oznacz jako dodane do indexu" → znikają z listy.
// Kolejna partia publish → na liście tylko NOWE (indexed_at IS NULL).
//
// Znacznik: thematic_node_content.indexed_at (migracja 20260613).
// Pomyłka? — przycisk "cofnij" przy wpisie w sekcji "W indexie" zeruje znacznik.
// =============================================
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Check,
  ClipboardCopy,
  Download,
  Link2,
  Loader2,
  RotateCcw,
  Send,
} from "lucide-react";
import { useThematicNodeContent } from "@/hooks/useThematicNodeContent";
import { useThematicNodes } from "@/hooks/useThematicNodes";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { uiLog } from "@/services/contentEngine/contentLogger";
import type { ThematicMap } from "@/types/thematicMaps";

interface IndexingPanelProps {
  map: ThematicMap;
}

interface PublishedRow {
  contentId: string;
  nodeId: string;
  url: string;
  title: string;
  publishedAt: string | null;
  indexedAt: string | null;
}

export function IndexingPanel({ map }: IndexingPanelProps) {
  const { contents, refreshContents } = useThematicNodeContent(map.id);
  const { nodes } = useThematicNodes(map.id);
  const { toast } = useToast();

  const [deselected, setDeselected] = useState<Set<string>>(new Set());
  const [marking, setMarking] = useState(false);
  const [unmarkingId, setUnmarkingId] = useState<string | null>(null);

  const titleByNodeId = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of nodes as Array<{ id: string; title?: string | null }>) {
      if (n.title) m.set(n.id, n.title);
    }
    return m;
  }, [nodes]);

  const published: PublishedRow[] = useMemo(() => {
    return contents
      .filter((c) => (c as any).engine_stage === "published" && (c as any).wp_post_url)
      .map((c) => ({
        contentId: c.id,
        nodeId: c.node_id,
        url: (c as any).wp_post_url as string,
        title: titleByNodeId.get(c.node_id) || (c as any).seo_title || c.node_id,
        publishedAt: (c as any).published_at || null,
        indexedAt: (c as any).indexed_at || null,
      }))
      .sort((a, b) => (b.publishedAt || "").localeCompare(a.publishedAt || ""));
  }, [contents, titleByNodeId]);

  const pending = useMemo(() => published.filter((p) => !p.indexedAt), [published]);
  const indexed = useMemo(() => published.filter((p) => p.indexedAt), [published]);

  // Default: wszystko zaznaczone — przechowujemy ODZNACZONE, więc nowe wpisy
  // po kolejnej publikacji są automatycznie zaznaczone bez syncowania stanu.
  const selected = useMemo(
    () => pending.filter((p) => !deselected.has(p.contentId)),
    [pending, deselected],
  );
  const urlsText = useMemo(() => selected.map((p) => p.url).join("\n"), [selected]);

  const toggleRow = (contentId: string) => {
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(contentId)) next.delete(contentId);
      else next.add(contentId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.length === pending.length) {
      setDeselected(new Set(pending.map((p) => p.contentId)));
    } else {
      setDeselected(new Set());
    }
  };

  const handleCopy = async () => {
    if (!urlsText) return;
    try {
      await navigator.clipboard.writeText(urlsText);
      toast({ title: "Skopiowano", description: `${selected.length} URL w schowku` });
    } catch {
      toast({ title: "Błąd", description: "Nie udało się skopiować — zaznacz textarea ręcznie", variant: "destructive" });
    }
  };

  const handleExportCsv = () => {
    if (selected.length === 0) return;
    const esc = (v: string) => `"${(v || "").replace(/"/g, '""')}"`;
    const lines = [
      "url,title,published_at",
      ...selected.map((p) => [esc(p.url), esc(p.title), esc(p.publishedAt || "")].join(",")),
    ];
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `indexer-${map.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    uiLog.public("Indexing", `Eksport CSV: ${selected.length} URL (mapa ${map.name})`);
  };

  const handleMarkIndexed = async () => {
    if (selected.length === 0 || marking) return;
    setMarking(true);
    try {
      const ids = selected.map((p) => p.contentId);
      const { error } = await (supabase as any)
        .from("thematic_node_content")
        .update({ indexed_at: new Date().toISOString() })
        .in("id", ids);
      if (error) throw error;
      uiLog.public("Indexing", `Oznaczono ${ids.length} URL jako dodane do indexu (mapa ${map.name})`);
      toast({ title: "Dodane do indexu", description: `${ids.length} URL oznaczonych — nie pojawią się ponownie` });
      setDeselected(new Set());
      await refreshContents();
    } catch (e) {
      toast({
        title: "Błąd",
        description: e instanceof Error ? e.message : "Nie udało się oznaczyć",
        variant: "destructive",
      });
    } finally {
      setMarking(false);
    }
  };

  const handleUnmark = async (row: PublishedRow) => {
    setUnmarkingId(row.contentId);
    try {
      const { error } = await (supabase as any)
        .from("thematic_node_content")
        .update({ indexed_at: null })
        .eq("id", row.contentId);
      if (error) throw error;
      await refreshContents();
    } catch (e) {
      toast({
        title: "Błąd",
        description: e instanceof Error ? e.message : "Nie udało się cofnąć",
        variant: "destructive",
      });
    } finally {
      setUnmarkingId(null);
    }
  };

  const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("pl-PL") : "—");

  return (
    <div className="space-y-6">
      {/* Header + stats */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Send className="h-5 w-5 text-emerald-500" />
            Indeksowanie — URL opublikowanych wpisów
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Skopiuj nowe URL-e do indexera, potem oznacz jako dodane — przy kolejnej partii zobaczysz tylko nowe.
          </p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="px-3 py-1">opublikowane: {published.length}</Badge>
          <Badge variant="outline" className="px-3 py-1 border-emerald-500/40 text-emerald-500 bg-emerald-500/10">
            w indexie: {indexed.length}
          </Badge>
          <Badge
            variant="outline"
            className={`px-3 py-1 ${pending.length > 0 ? "border-amber-500/40 text-amber-500 bg-amber-500/10" : ""}`}
          >
            do dodania: {pending.length}
          </Badge>
        </div>
      </div>

      {/* Do dodania */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Do dodania do indexu ({pending.length})</CardTitle>
              <CardDescription>
                Prawdziwe URL-e z WordPressa (wp_post_url) — jeszcze nie zgłoszone do indexera
              </CardDescription>
            </div>
            {pending.length > 0 && (
              <Button variant="ghost" size="sm" onClick={toggleAll} className="text-xs">
                {selected.length === pending.length ? "Odznacz wszystkie" : "Zaznacz wszystkie"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {pending.length === 0 ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2 py-4">
              <Check className="h-4 w-4 text-emerald-500" />
              {published.length === 0
                ? "Brak opublikowanych wpisów — najpierw opublikuj artykuły w kroku Publikacja."
                : "Wszystkie opublikowane wpisy są już dodane do indexu. Opublikuj kolejne — pojawią się tutaj."}
            </div>
          ) : (
            <>
              {/* Checkbox list */}
              <div className="max-h-64 overflow-y-auto border rounded-md divide-y divide-border/40">
                {pending.map((p) => (
                  <label
                    key={p.contentId}
                    className="flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-muted/30 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={!deselected.has(p.contentId)}
                      onChange={() => toggleRow(p.contentId)}
                      className="mt-1 rounded border-gray-300"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium truncate">{p.title}</span>
                      <span className="block text-xs text-muted-foreground font-mono truncate">
                        {p.url} · publ. {fmtDate(p.publishedAt)}
                      </span>
                    </span>
                  </label>
                ))}
              </div>

              {/* Textarea do kopiowania */}
              <Textarea
                readOnly
                value={urlsText}
                rows={Math.min(8, Math.max(3, selected.length))}
                className="font-mono text-xs"
                onFocus={(e) => e.currentTarget.select()}
                placeholder="Zaznacz wpisy powyżej…"
              />

              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={handleCopy} disabled={selected.length === 0}>
                  <ClipboardCopy className="h-4 w-4 mr-2" />
                  Kopiuj ({selected.length})
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={selected.length === 0}>
                  <Download className="h-4 w-4 mr-2" />
                  Eksport CSV
                </Button>
                <Button
                  size="sm"
                  onClick={handleMarkIndexed}
                  disabled={selected.length === 0 || marking}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {marking ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Oznacz jako dodane do indexu ({selected.length})
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* W indexie */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">W indexie ({indexed.length})</CardTitle>
          <CardDescription>Oznaczone jako zgłoszone — pomyłka? cofnij i wróci na listę powyżej</CardDescription>
        </CardHeader>
        <CardContent>
          {indexed.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">Jeszcze nic nie oznaczono.</p>
          ) : (
            <div className="max-h-72 overflow-y-auto border rounded-md divide-y divide-border/40">
              {indexed.map((p) => (
                <div key={p.contentId} className="flex items-center gap-2.5 px-3 py-2">
                  <Link2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span className="block text-sm truncate">{p.title}</span>
                    <span className="block text-xs text-muted-foreground font-mono truncate">
                      {p.url} · index {fmtDate(p.indexedAt)}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleUnmark(p)}
                    disabled={unmarkingId === p.contentId}
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground shrink-0"
                    title="Cofnij — wróci na listę do dodania"
                  >
                    {unmarkingId === p.contentId ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RotateCcw className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
